import { useEffect, useState } from 'react';

import {
  STORAGE_SIGNED_URL_API_URL,
  STORAGE_UPLOAD_API_URL,
} from '@/constants/apis';
import { apiManager } from '@/helpers/api.manager';
import { toast } from '@/hooks/ui/use-toast';
import logger from '@/services/logger.service';

/**
 * Storage buckets are private. DB rows keep the public-form URL; every reader
 * exchanges it for a short-lived signed URL via the zor backend at read time.
 * Contract: POST /v1/storage/signed-url {url, download?, ttl_seconds?}
 *   -> {signed_url, expires_in}  (non-Supabase URLs come back unchanged).
 * If the contract shifts, only getSignedUrl/uploadToStorage below need edits.
 */

export class StorageError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'StorageError';
    this.status = status;
  }
}

export interface SignedUrlOptions {
  download?: boolean;
  ttlSeconds?: number;
}

const SUPABASE_STORAGE_PATH = '/storage/v1/object/';

/** True for Supabase storage URLs (public form or already signed). */
export const isSupabaseStorageUrl = (url?: string | null): boolean =>
  !!url && url.includes(SUPABASE_STORAGE_PATH);

const isAlreadySigned = (url: string) =>
  url.includes(`${SUPABASE_STORAGE_PATH}sign/`) || url.includes('token=');

const messageForStatus = (status?: number): string => {
  if (status === 403) return "You don't have access to this file";
  if (status === 404) return 'File not found';
  return 'Could not open the file. Please try again';
};

const extractMessage = (res: unknown): string | undefined => {
  const data = (res as { response?: { data?: Record<string, unknown> } })
    ?.response?.data;
  const detail = data?.detail ?? data?.message;
  return typeof detail === 'string' ? detail : undefined;
};

/**
 * Returns a URL that can be opened/fetched now. Non-storage and already
 * signed URLs are returned as is without a network call.
 * Note: apiManager resolves (does not reject) on HTTP errors.
 */
export const getSignedUrl = async (
  url: string,
  { download, ttlSeconds }: SignedUrlOptions = {}
): Promise<string> => {
  if (!url || !isSupabaseStorageUrl(url) || isAlreadySigned(url)) {
    return url;
  }

  const res = await apiManager.post(STORAGE_SIGNED_URL_API_URL, {
    url,
    ...(download !== undefined && { download }),
    ...(ttlSeconds !== undefined && { ttl_seconds: ttlSeconds }),
  });

  const status = (res as { status?: number })?.status;
  const response = (res as { response?: { status?: number } })?.response;
  if (res instanceof Error || !status || status >= 400) {
    const code = response?.status ?? status;
    throw new StorageError(extractMessage(res) ?? messageForStatus(code), code);
  }

  // Accept both {signed_url} and the {data: {signed_url}} envelope.
  const body = res.data as {
    signed_url?: string;
    data?: { signed_url?: string };
  };
  const signed = body?.signed_url ?? body?.data?.signed_url;
  if (!signed) {
    throw new StorageError('Invalid signed URL response');
  }
  return signed;
};

const showSignError = (error: unknown) => {
  const status = error instanceof StorageError ? error.status : undefined;
  toast({
    title: status === 404 ? 'File not found' : 'Cannot open file',
    description:
      status === 403 || status === 404
        ? messageForStatus(status)
        : error instanceof Error
          ? error.message
          : messageForStatus(),
    variant: 'destructive',
  });
};

/**
 * Opens a stored file in a new tab. The tab is opened synchronously (to keep
 * popup blockers happy) and closed again if signing fails, so the user never
 * sees a blank tab. Shows a toast on failure.
 */
export const openStoredFile = async (
  url: string,
  options: SignedUrlOptions = {}
): Promise<boolean> => {
  if (!url) return false;

  if (!isSupabaseStorageUrl(url) || isAlreadySigned(url)) {
    window.open(url, '_blank', 'noopener,noreferrer');
    return true;
  }

  const tab = window.open('', '_blank');
  if (tab) tab.opener = null;

  try {
    const signed = await getSignedUrl(url, options);
    if (tab) {
      tab.location.href = signed;
    } else {
      window.open(signed, '_blank', 'noopener,noreferrer');
    }
    return true;
  } catch (error) {
    tab?.close();
    showSignError(error);
    return false;
  }
};

/** Downloads a stored file as a blob with the given file name. */
export const downloadStoredFile = async (
  url: string,
  name: string,
  options: Omit<SignedUrlOptions, 'download'> = {}
): Promise<boolean> => {
  if (!url) return false;

  try {
    const signed = await getSignedUrl(url, { ...options, download: true });
    const response = await fetch(signed);
    if (!response.ok)
      throw new StorageError('Download failed', response.status);

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', name);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
    return true;
  } catch (error) {
    console.error('Error downloading file:', error);
    showSignError(error);
    return false;
  }
};

/**
 * Hook for embeds (react-pdf, iframes) that need a signed URL before render.
 * `url` is undefined until signing finishes; `error` is set on 403/404.
 */
export const useSignedUrl = (
  url: string | null | undefined,
  options: Pick<SignedUrlOptions, 'ttlSeconds'> = {}
) => {
  const [signedUrl, setSignedUrl] = useState<string | undefined>();
  const [error, setError] = useState<Error | null>(null);
  const { ttlSeconds } = options;

  useEffect(() => {
    let cancelled = false;
    setSignedUrl(undefined);
    setError(null);
    if (!url) return;

    getSignedUrl(url, { ttlSeconds })
      .then((signed) => {
        if (!cancelled) setSignedUrl(signed);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e);
        showSignError(e);
      });

    return () => {
      cancelled = true;
    };
  }, [url, ttlSeconds]);

  return {
    url: signedUrl,
    error,
    isLoading: !!url && !signedUrl && !error,
  };
};

export interface UploadedDocument {
  file_size: number;
  file_url: string;
}

export interface UploadToStorageOptions {
  file: File;
  bucketName: string;
  folderName: string;
  setIsUploading?: (isUploading: boolean) => void;
}

/**
 * Uploads through the zor backend (no direct Supabase access from the
 * browser). The backend forces the path under the caller's org and returns the
 * public-form URL, which is what we store in the DB.
 */
export const uploadToStorage = async ({
  file,
  bucketName,
  folderName,
  setIsUploading,
}: UploadToStorageOptions): Promise<UploadedDocument | null> => {
  setIsUploading?.(true);

  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('bucket', bucketName);
    if (folderName) formData.append('organization_id', folderName);

    const res = await apiManager.post(STORAGE_UPLOAD_API_URL, formData);
    const response = (res as { response?: { status?: number } })?.response;
    if (res instanceof Error || res.status !== 200 || !res.data?.success) {
      throw new StorageError(
        extractMessage(res) ?? res.data?.message ?? 'Upload failed',
        response?.status ?? res.status
      );
    }

    const payload = res.data.data;
    const raw = payload?.file_url;
    const fileUrl: string =
      typeof raw === 'string' ? raw : (raw?.publicUrl ?? '');
    if (!fileUrl) throw new StorageError('Invalid upload response');

    toast({ title: 'File uploaded successfully', variant: 'default' });

    return { file_size: payload.file_size ?? file.size, file_url: fileUrl };
  } catch (error) {
    logger.error(
      `Failed to upload file to storage bucket: ${bucketName}`,
      error as Error,
      { fileName: file.name, folderName, bucketName }
    );
    throw error;
  } finally {
    setIsUploading?.(false);
  }
};
